/**
 * The per-frame half of the experience: it owns the guide entity, drives the
 * avatar and the reveal, and keeps the speech bubble beside the guide and
 * facing the viewer.
 *
 * A system rather than a service because all of this is frame work, and IWSDK
 * gives systems the delta, the camera and the world for free.
 */
import {
  Object3D,
  PanelDocument,
  Vector3,
  XRAnchor,
  createSystem,
  type Entity,
} from '@iwsdk/core';
import { UIWindow } from '@realitycollective/iwsdk-uiextensions';

import { CONFIG } from './config.js';
import { AvatarController } from './avatar/AvatarController.js';
import { installLighting, type LightRig } from './avatar/lighting.js';
import { loadAvatar } from './avatar/loadAvatar.js';
import {
  DEFAULT_PROFILE,
  loadProfile,
  profileBaseUrl,
  reportProfileCoverage,
  resolveAvatarId,
  type AvatarProfile,
} from './avatar/profile.js';
import { placementStore, savePose, type Pose } from './placement/placementStore.js';
import { chat } from './chat/chatRuntime.js';
import { uiLog } from './ui/uiLog.js';

/** Window id of the speech bubble, matched when its panel qualifies. */
const BUBBLE_WINDOW_ID = 'guide-bubble';

export class GuideSystem extends createSystem({
  // `onPanelReady` is deliberately engine-agnostic and carries no entity, so
  // the bubble's entity is discovered the IWSDK-native way instead.
  windows: { required: [UIWindow, PanelDocument] },
}) {
  private controller?: AvatarController;
  private profile?: AvatarProfile;
  private lights?: LightRig;
  private guideEntity?: Entity;
  private bubbleEntity?: Entity;
  private anchored = false;
  private greeted = false;

  private readonly cameraPosition = new Vector3();
  private readonly bubbleLookTarget = new Vector3();
  private readonly bubbleWorldPosition = new Vector3();

  override init(): void {
    void this.spawnGuide();
    this.watchConversation();

    const considerWindow = (entity: Entity): void => {
      if (entity.getValue(UIWindow, 'windowId') !== BUBBLE_WINDOW_ID) return;
      this.adoptBubble(entity);
    };

    this.cleanupFuncs.push(
      placementStore.subscribe((state) => {
        if (state.phase === 'placed' && state.pose) this.applyPose(state.pose);
      }),
      this.queries.windows.subscribe('qualify', considerWindow),
    );
    // Cover the panel having qualified before this system initialised.
    this.queries.windows.entities.forEach(considerWindow);
  }

  override update(delta: number): void {
    // The reveal is what makes the mouth move, so it must tick before the
    // avatar reads `isSpeaking` — otherwise the mouth lags a frame behind.
    chat.playback.advance(delta);
    this.controller?.setSpeaking(chat.playback.isSpeaking);

    if (this.controller) {
      this.camera.getWorldPosition(this.cameraPosition);
      this.controller.update(delta, this.cameraPosition);
      // After the controller has turned the model, so the key light lands on
      // this frame's facing rather than the previous one's.
      this.lights?.setFacing(this.controller.facing);
    }

    this.updateBubble();
  }

  /** Attach the speech bubble entity once its panel exists. */
  private adoptBubble(entity: Entity): void {
    if (this.bubbleEntity === entity) return;
    this.bubbleEntity = entity;
    const object = entity.object3D;
    const guide = this.guideEntity?.object3D;
    if (!object || !guide) return;

    // Parenting to the guide gives the bubble the guide's anchor stability for
    // free. Do NOT also give it an XRAnchor — that would `attach()` it straight
    // back out of this hierarchy and into the global anchor group.
    guide.add(object);
    object.position.set(...(CONFIG.ui.bubbleOffset as [number, number, number]));
  }

  /**
   * Drive gesture from the conversation.
   *
   * This is the wiring the reference project is missing: it carries
   * `animationClip` all the way into message metadata and then never reads it,
   * so its guide only ever idles and most of the model's clips are unreachable.
   * Two sources feed it here — the exact clip when the backend names one, and a
   * hint-derived fallback when it does not, so the guide stays expressive
   * against a backend that sends neither.
   */
  private watchConversation(): void {
    let lastGuideId: string | null = null;
    let wasSending = false;

    this.cleanupFuncs.push(
      chat.conversation.subscribe((state) => {
        const sending = state.status === 'sending';
        if (sending && !wasSending) {
          this.controller?.playGesture(this.profile?.clips.thinking);
        }
        wasSending = sending;

        const last = state.messages[state.messages.length - 1];
        if (!last || last.role !== 'guide' || last.id === lastGuideId) return;
        // Wait for the text to land: a message appended empty and filled by
        // streaming would otherwise trigger the gesture a beat before the guide
        // actually starts saying anything.
        if (last.text.length === 0) return;
        lastGuideId = last.id;

        const meta = state.meta[last.id];
        const clip =
          meta?.animationClip ||
          (meta?.animationHint ? this.profile?.clips.byHint[meta.animationHint] : '');
        if (clip) this.controller?.playGesture(clip);
      }),
    );
  }

  private async spawnGuide(): Promise<void> {
    const holder = new Object3D();
    holder.name = 'guide';
    holder.visible = false;
    this.guideEntity = this.world.createTransformEntity(holder);

    try {
      const id = resolveAvatarId();
      const profile = await loadProfile(id);
      this.profile = profile;

      const avatar = await loadAvatar(`${profileBaseUrl(id)}/${profile.model}`, profile);
      holder.add(avatar.scene);
      this.controller = new AvatarController(avatar, profile);
      this.controller.applyScale();

      uiLog.push(
        `avatar "${profile.name}" loaded — ${avatar.clips.size} clips, ` +
          `${avatar.mouth.length} mouth / ${avatar.blink.length} blink targets`,
      );
      // Surfaces any configured name the model does not actually have, which
      // otherwise just means an animation that never plays.
      reportProfileCoverage(profile, avatar.available);
    } catch (error) {
      // The experience is still usable without a model — the guide becomes a
      // disembodied voice rather than nothing at all.
      uiLog.push(`avatar failed to load: ${error instanceof Error ? error.message : error}`);
    }

    // Outside the try: the model can fail to load, but if it did load it must
    // be lit. Nothing in the scene emits light until this runs.
    this.lights = installLighting(this.world, (this.profile ?? DEFAULT_PROFILE).lighting);

    this.cleanupFuncs.push(() => {
      this.controller?.dispose();
      this.lights?.dispose();
    });
  }

  private applyPose(pose: Pose): void {
    const object = this.guideEntity?.object3D;
    if (!object) return;

    object.position.set(...pose.position);
    object.quaternion.set(...pose.quaternion);
    // The reticle lies flat against the surface; the guide should stand on it,
    // not lie on it. Yaw comes from the billboard each frame anyway.
    object.rotation.set(0, object.rotation.y, 0);
    object.visible = true;

    this.controller?.startEntrance();

    if (!this.anchored) {
      // Added AFTER positioning: attach() preserves the world transform, so
      // anchoring now keeps the guide exactly where the user put it.
      this.guideEntity?.addComponent(XRAnchor);
      this.anchored = true;
    }
    savePose(pose);

    if (!this.greeted) {
      this.greeted = true;
      chat.greet();
    }
  }

  /**
   * Yaw-only billboard for the bubble. It is parented to the guide, which is
   * itself yawing to face the viewer, so the bubble is counter-rotated in world
   * space rather than inheriting a second rotation.
   */
  private updateBubble(): void {
    const object = this.bubbleEntity?.object3D;
    if (!object?.visible || !object.parent) return;

    object.getWorldPosition(this.bubbleWorldPosition);
    this.camera.getWorldPosition(this.bubbleLookTarget);
    this.bubbleLookTarget.y = this.bubbleWorldPosition.y;
    if (this.bubbleLookTarget.distanceToSquared(this.bubbleWorldPosition) < 1e-6) return;

    object.lookAt(this.bubbleLookTarget);
  }
}
